import React, { useState } from 'react';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Search, Filter, Loader2, ListFilter } from 'lucide-react';

const EMPTY_FILTERS = {
  estado: '', prioridad: '', asignacion: '', cuenta_bloqueada: '',
  es_externo: '', min_alertas: '', tiene_vinculos: '', ros_en_proceso: '', waya_verificacion: '',
  fecha_desde: '', fecha_hasta: '', cvus: '', cuits: '', solicitud_bloqueo: '',
};

// Barra de filtros del listado de casos
const CasesFilters = ({
  filters,
  setFilters,
  searchTerm,
  setSearchTerm,
  usuarios,
  estados,
  prioridades,
  selectedLinkIds,
  onAnalizarVinculacion,
  vinculacionLoading,
}) => {
  const [cvuDialogOpen, setCvuDialogOpen] = useState(false);
  const [cvuText, setCvuText] = useState('');
  const cvuCount = (filters.cvus || '').split(/[\n,;\s]+/).filter(Boolean).length;
  const [cuitDialogOpen, setCuitDialogOpen] = useState(false);
  const [cuitText, setCuitText] = useState('');
  const cuitCount = (filters.cuits || '').split(/[\n,;\s]+/).filter(Boolean).length;
  return (
    <Card className="p-4 mb-6 bg-white">
      <div className="flex flex-wrap gap-4 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-[320px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar cliente, CUIT, CVU o ID de caso..."
            className="pl-9"
            style={{ backgroundColor: '#fff' }}
            data-testid="search-cases-input"
          />
        </div>
        <Filter size={18} className="text-slate-400" />
        <Select value={filters.estado || "all"} onValueChange={(v) => setFilters(f => ({ ...f, estado: v === "all" ? "" : v }))}>
          <SelectTrigger className="w-48" style={{ backgroundColor: '#fff' }}>
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {estados.map(e => (
              <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.prioridad || "all"} onValueChange={(v) => setFilters(f => ({ ...f, prioridad: v === "all" ? "" : v }))}>
          <SelectTrigger className="w-48" style={{ backgroundColor: '#fff' }}>
            <SelectValue placeholder="Prioridad" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las prioridades</SelectItem>
            {prioridades.map(p => (
              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.asignacion || "all"} onValueChange={(v) => setFilters(f => ({ ...f, asignacion: v === "all" ? "" : v }))}>
          <SelectTrigger className="w-56" style={{ backgroundColor: '#fff' }}>
            <SelectValue placeholder="Asignación" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="mis_casos">Mis casos</SelectItem>
            <SelectItem value="sin_asignar">Sin asignar</SelectItem>
            <SelectItem value="asignados">Asignados a otros</SelectItem>
            {usuarios.length > 0 && (
              <>
                <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">
                  Por usuario
                </div>
                {usuarios.map(u => (
                  <SelectItem key={`user-${u.id}`} value={`user:${u.id}`}>
                    {u.nombre}
                  </SelectItem>
                ))}
              </>
            )}
          </SelectContent>
        </Select>
        <Select value={filters.cuenta_bloqueada || "all"} onValueChange={(v) => setFilters(f => ({ ...f, cuenta_bloqueada: v === "all" ? "" : v }))}>
          <SelectTrigger className="w-52" style={{ backgroundColor: '#fff' }}>
            <SelectValue placeholder="Bloqueo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las cuentas</SelectItem>
            <SelectItem value="bloqueada">Cuenta Bloqueada</SelectItem>
            <SelectItem value="no_bloqueada">Sin bloqueo</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.es_externo || "all"} onValueChange={(v) => setFilters(f => ({ ...f, es_externo: v === "all" ? "" : v }))}>
          <SelectTrigger className="w-44" style={{ backgroundColor: '#fff' }}>
            <SelectValue placeholder="Origen" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los orígenes</SelectItem>
            <SelectItem value="externo">Externos</SelectItem>
            <SelectItem value="interno">Internos (Padrón)</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.min_alertas || "all"} onValueChange={(v) => setFilters(f => ({ ...f, min_alertas: v === "all" ? "" : v }))}>
          <SelectTrigger className="w-40" style={{ backgroundColor: '#fff' }}>
            <SelectValue placeholder="Alertas" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las alertas</SelectItem>
            <SelectItem value="exact1">Solo 1 alerta</SelectItem>
            <SelectItem value="exact2">Solo 2 alertas</SelectItem>
            <SelectItem value="1">1+ alerta</SelectItem>
            <SelectItem value="2">2+ alertas</SelectItem>
            <SelectItem value="3">3+ alertas</SelectItem>
            <SelectItem value="5">5+ alertas</SelectItem>
            <SelectItem value="10">10+ alertas</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.tiene_vinculos || "all"} onValueChange={(v) => setFilters(f => ({ ...f, tiene_vinculos: v === "all" ? "" : v }))}>
          <SelectTrigger className="w-44" style={{ backgroundColor: '#fff' }} data-testid="vinculos-filter-trigger">
            <SelectValue placeholder="Vínculos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los vínculos</SelectItem>
            <SelectItem value="si">Con casos vinculados</SelectItem>
            <SelectItem value="no">Sin vínculos</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.ros_en_proceso || "all"} onValueChange={(v) => setFilters(f => ({ ...f, ros_en_proceso: v === "all" ? "" : v }))}>
          <SelectTrigger className="w-48" style={{ backgroundColor: '#fff' }} data-testid="ros-proceso-filter-trigger">
            <SelectValue placeholder="ROS" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos (ROS)</SelectItem>
            <SelectItem value="si">Con ROS en proceso</SelectItem>
            <SelectItem value="no">Sin ROS en proceso</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.waya_verificacion || "all"} onValueChange={(v) => setFilters(f => ({ ...f, waya_verificacion: v === "all" ? "" : v }))}>
          <SelectTrigger className="w-52" style={{ backgroundColor: '#fff' }} data-testid="waya-verif-filter-trigger">
            <SelectValue placeholder="Verificación Waya" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos (Waya)</SelectItem>
            <SelectItem value="con_cvu">⚠️ Debía baja y sigue activa</SelectItem>
            <SelectItem value="bloqueada">Bloqueadas en Waya</SelectItem>
            <SelectItem value="sin_cvu">Sin CVU (OK)</SelectItem>
            <SelectItem value="activa_info">Activa (cierre sin baja)</SelectItem>
            <SelectItem value="error">Error de verificación</SelectItem>
            <SelectItem value="sin_verificar">Sin verificar</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.solicitud_bloqueo || "all"} onValueChange={(v) => setFilters(f => ({ ...f, solicitud_bloqueo: v === "all" ? "" : v }))}>
          <SelectTrigger className="w-52" style={{ backgroundColor: '#fff' }} data-testid="solicitud-bloqueo-filter-trigger">
            <SelectValue placeholder="Solicitud Bloqueo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas (Solicitud)</SelectItem>
            <SelectItem value="con">Con solicitud de bloqueo</SelectItem>
            <SelectItem value="sin">Sin solicitud de bloqueo</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1" data-testid="fecha-filter">
          <span className="text-xs text-slate-500">Desde</span>
          <Input
            type="date"
            value={filters.fecha_desde || ''}
            onChange={(e) => setFilters(f => ({ ...f, fecha_desde: e.target.value }))}
            className="w-40"
            style={{ backgroundColor: '#fff' }}
            data-testid="fecha-desde-input"
          />
          <span className="text-xs text-slate-500">Hasta</span>
          <Input
            type="date"
            value={filters.fecha_hasta || ''}
            onChange={(e) => setFilters(f => ({ ...f, fecha_hasta: e.target.value }))}
            className="w-40"
            style={{ backgroundColor: '#fff' }}
            data-testid="fecha-hasta-input"
          />
        </div>
        <Button
          variant="outline"
          onClick={() => { setCvuText(filters.cvus || ''); setCvuDialogOpen(true); }}
          className={cvuCount > 0 ? 'border-blue-500 text-blue-700' : ''}
          data-testid="cvus-filter-btn"
        >
          <ListFilter size={14} className="mr-1" />
          {cvuCount > 0 ? `CVUs (${cvuCount})` : 'Filtrar por CVUs'}
        </Button>
        <Button
          variant="outline"
          onClick={() => { setCuitText(filters.cuits || ''); setCuitDialogOpen(true); }}
          className={cuitCount > 0 ? 'border-blue-500 text-blue-700' : ''}
          data-testid="cuits-filter-btn"
        >
          <ListFilter size={14} className="mr-1" />
          {cuitCount > 0 ? `CUITs (${cuitCount})` : 'Filtrar por CUITs'}
        </Button>
        <Button
          variant="outline"
          onClick={() => { setFilters({ ...EMPTY_FILTERS }); setSearchTerm(''); }}
          data-testid="clear-filters-btn"
        >
          Limpiar
        </Button>
        {selectedLinkIds.length >= 2 && (
          <Button
            onClick={onAnalizarVinculacion}
            disabled={vinculacionLoading}
            className="bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="analizar-vinculacion-btn"
          >
            {vinculacionLoading ? (
              <><Loader2 size={14} className="animate-spin mr-1" /> Analizando...</>
            ) : (
              <>{selectedLinkIds.length} seleccionados — Analizar Vinculación</>
            )}
          </Button>
        )}
        {selectedLinkIds.length === 1 && (
          <span className="text-xs text-slate-400 self-center">Seleccioná otro caso para analizar vinculación</span>
        )}
      </div>

      <Dialog open={cvuDialogOpen} onOpenChange={setCvuDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="cvus-filter-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ListFilter size={18} /> Filtrar casos por lista de CVUs</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">Pegá una lista de CVUs (o CUITs), uno por línea o separados por coma. Se mostrarán los casos que contengan alguna de esas cuentas.</p>
          <Textarea
            rows={8}
            value={cvuText}
            onChange={(e) => setCvuText(e.target.value)}
            placeholder={'0000357301000000819036\n0000357301000000819037'}
            className="font-mono text-xs"
            data-testid="cvus-filter-textarea"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCvuText(''); setFilters(f => ({ ...f, cvus: '' })); setCvuDialogOpen(false); }} data-testid="cvus-filter-clear">
              Quitar filtro
            </Button>
            <Button onClick={() => { setFilters(f => ({ ...f, cvus: cvuText })); setCvuDialogOpen(false); }} className="bg-blue-600 hover:bg-blue-700" data-testid="cvus-filter-apply">
              Aplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cuitDialogOpen} onOpenChange={setCuitDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="cuits-filter-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ListFilter size={18} /> Filtrar casos por lista de CUITs</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">Pegá una lista de CUITs (con o sin guiones), uno por línea o separados por coma. El formato no importa: se normaliza automáticamente.</p>
          <Textarea
            rows={8}
            value={cuitText}
            onChange={(e) => setCuitText(e.target.value)}
            placeholder={'20-12345678-9\n30712345678'}
            className="font-mono text-xs"
            data-testid="cuits-filter-textarea"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCuitText(''); setFilters(f => ({ ...f, cuits: '' })); setCuitDialogOpen(false); }} data-testid="cuits-filter-clear">
              Quitar filtro
            </Button>
            <Button onClick={() => { setFilters(f => ({ ...f, cuits: cuitText })); setCuitDialogOpen(false); }} className="bg-blue-600 hover:bg-blue-700" data-testid="cuits-filter-apply">
              Aplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default CasesFilters;
